/*!
 * Copyright © 2020-2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { MatchValidator, notEmptyRequiredValidator } from '../../../shared/validators';
import { EApiRequestStatus, IPasswordConfirmationForm } from '../../models';
import { SendVerificationCodeRequest } from '../../store/actions';
import { getResendCodeRequestStatus, getSetPasswordRequestStatus, getUsername, getVerifyAccountRequestStatus } from '../../store/selectors';
import { IAccountState } from '../../store/state';

@Component({
  selector: 'app-set-your-password',
  templateUrl: './set-your-password.component.html',
  styleUrls: ['./set-your-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetYourPasswordComponent implements OnInit, OnDestroy {
  form: FormGroup<IPasswordConfirmationForm>;

  isRequestFailure$: Observable<boolean>;
  setPasswordRequestStatus$: Observable<EApiRequestStatus>;
  apiRequestStatus = EApiRequestStatus;

  usernameSubscription: Subscription | null = null;
  passwordSubscription: Subscription | null = null;

  email: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
    validators: [notEmptyRequiredValidator, Validators.maxLength(100), Validators.email]
  });
  password: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
    validators: [notEmptyRequiredValidator, Validators.minLength(8)]
  });
  passwordConfirm: FormControl<string> = new FormControl<string>('', {
    nonNullable: true,
    validators: [notEmptyRequiredValidator, Validators.minLength(8), MatchValidator(this.password)]
  });

  username = '';

  constructor(private readonly store: Store<IAccountState>, private readonly activatedRoute: ActivatedRoute) {
    this.form = new FormGroup<IPasswordConfirmationForm>({
      password: this.password,
      passwordConfirm: this.passwordConfirm
    });

    this.addEmailInputIfNotProvided();

    this.setPasswordRequestStatus$ = this.store.pipe(select(getSetPasswordRequestStatus));
    this.isRequestFailure$ = combineLatest([
      this.store.pipe(select(getResendCodeRequestStatus)),
      this.store.pipe(select(getVerifyAccountRequestStatus)),
      this.setPasswordRequestStatus$
    ]).pipe(map(requestStatus => requestStatus.includes(EApiRequestStatus.Failure)));
  }

  ngOnInit() {
    this.passwordSubscription = this.subscribeToPasswordUpdates();
  }

  ngOnDestroy() {
    if (this.passwordSubscription) {
      this.passwordSubscription.unsubscribe();
    }

    if (this.usernameSubscription) {
      this.usernameSubscription.unsubscribe();
    }
  }

  addEmailInputIfNotProvided(): void {
    // the ActivatedRoute subscription is closed when the router destroys a routed component
    // so we do not need to unsubscribe.
    this.activatedRoute.queryParams.subscribe(({ email }: Params) => {
      if (email) {
        this.username = email;
      } else {
        this.usernameSubscription = this.store.pipe(select(getUsername)).subscribe(username => {
          this.email.setValue(username);
          this.form.addControl('email', this.email);
        });
      }
    });
  }

  subscribeToPasswordUpdates(): Subscription {
    return this.password.valueChanges.subscribe(() => {
      const invalidControl = !!(this.password.invalid && this.passwordConfirm.dirty);
      const invalidControlToMatch = !!(this.passwordConfirm.invalid && this.passwordConfirm.dirty);

      if (invalidControl || invalidControlToMatch) {
        this.passwordConfirm.setErrors({ mismatch: true });
      }

      this.passwordConfirm.updateValueAndValidity();
    });
  }

  initialiseRequest(): void {
    this.store.dispatch(
      SendVerificationCodeRequest({
        username: this.email.value || this.username,
        password: this.password.value || ''
      })
    );
  }
}
